/**
 * Fetches GET /config and caches in localStorage. Call `fetchAndCache()` when you need fresh data (e.g. after login from your app shell).
 */

import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, switchMap, tap, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { API_ENDPOINTS, STORAGE_KEYS } from '../constants';
import { StorageUtil } from '../utils';

/** Single config row from API (aligned with system-configuration feature). */
export interface CachedSystemConfigItem {
  id: number;
  uuid: string;
  module: string | null;
  values: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface SystemConfigListMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

function normalizeList(raw: unknown): { data: CachedSystemConfigItem[]; meta: SystemConfigListMeta } {
  const r =
    raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  const rawData = r ? r['data'] : undefined;
  const data = (Array.isArray(rawData) ? rawData : []) as CachedSystemConfigItem[];
  const m = r ? (r['meta'] as SystemConfigListMeta | undefined) : undefined;
  const meta: SystemConfigListMeta = m ?? {
    total: data.length,
    page: 1,
    pageSize: data.length || 10,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  };
  return { data, meta };
}

@Injectable({
  providedIn: 'root',
})
export class SystemConfigCacheService {
  private readonly http = inject(HttpClient);

  private readonly itemsSignal = signal<CachedSystemConfigItem[]>(this.loadFromStorage());
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);

  readonly items = this.itemsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  private static readonly PAGE_SIZE = 100;

  /**
   * Fetch all config pages from API and persist to localStorage.
   */
  fetchAndCache(): Observable<CachedSystemConfigItem[]> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    return this.loadAllPages(1, []).pipe(
      tap((items) => {
        this.itemsSignal.set(items);
        StorageUtil.setLocal(STORAGE_KEYS.SYSTEM_CONFIG, items);
        this.loadingSignal.set(false);
      }),
      catchError((err) => {
        const msg = err?.errorMessage || err?.error?.message || 'Failed to load system config';
        this.errorSignal.set(msg);
        this.loadingSignal.set(false);
        return throwError(() => err);
      })
    );
  }

  /** Config entries from memory (hydrated from storage on init). */
  getCachedItems(): CachedSystemConfigItem[] {
    return this.itemsSignal();
  }

  /** First entry for a module, or null. */
  getByModule(module: string): CachedSystemConfigItem | null {
    const key = module?.trim().toUpperCase();
    return this.itemsSignal().find((i) => (i.module ?? '').toUpperCase() === key) ?? null;
  }

  clearCache(): void {
    this.itemsSignal.set([]);
    this.errorSignal.set(null);
    StorageUtil.removeLocal(STORAGE_KEYS.SYSTEM_CONFIG);
  }

  private loadAllPages(page: number, acc: CachedSystemConfigItem[]): Observable<CachedSystemConfigItem[]> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(SystemConfigCacheService.PAGE_SIZE));

    return this.http
      .get<unknown>(`${environment.apiUrl}${API_ENDPOINTS.SYSTEM_CONFIG.BASE}`, { params })
      .pipe(
        switchMap((raw) => {
          const { data, meta } = normalizeList(raw);
          const merged = [...acc, ...data];
          if (meta.hasNext && data.length > 0) {
            return this.loadAllPages(page + 1, merged);
          }
          return of(merged);
        })
      );
  }

  private loadFromStorage(): CachedSystemConfigItem[] {
    return StorageUtil.getLocal<CachedSystemConfigItem[]>(STORAGE_KEYS.SYSTEM_CONFIG) ?? [];
  }
}
